import { TOOLBAR_CONTROL_HEIGHT } from '@repo/ui'
import React, { useState, useEffect, useRef } from "react";

import {
  PlusIcon,
  XIcon,
  ArrowUpDownIcon,
  GripVerticalIcon,
} from "lucide-react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  type DroppableProvided,
  type DroppableStateSnapshot,
  type DraggableLocation,
} from "@hello-pangea/dnd";

import {
  alpha,
  Stack,
  Box,
  Typography,
  Button as MuiButton,
  IconButton,
  ButtonGroup,
  Menu,
  useTheme,
  MenuItem,
  Collapse,
  Tooltip,
  Zoom,
} from "@mui/material/";

import { MenuButton, Button, getSortDirectionLabels } from "@repo/ui";
import { Select } from "components";
// Direct file import, not the `components` barrel — this file is part of that barrel.
import { toolbarClearEdgeSx, toolbarClearSx, toolbarLabelSx, toolbarStartIconSx } from "../ActionLine/toolbarCollapse";
import { hasMuiXLicense } from "config/muiLicense";

export interface SortProps {
  apiRef?: any;
  disabled?: boolean;
  sort?: [];
  columns?: [];
  /**
   * The no-`apiRef` fallback. Called with FOUR different shapes, and they are listed
   * here because the previous `() => void` declared none of them — which is how a
   * `(field, null, true)` removal that no consumer implements sat undetected in
   * `handleRemoveSort`, and how `handleDragEnd` was allowed to skip the fallback
   * entirely. TypeScript cannot check a signature that takes no arguments.
   *
   *   onChange('all', 'clear', true)          clear every sort
   *   onChange(field, 'asc' | 'desc', true)   replace the model with this one sort
   *   onChange(field, 'asc' | 'desc', false)  add / update this field's direction
   *   onChange(SortItem[])                    replace the whole model (remove, reorder)
   *
   * All five non-`apiRef` consumers implement all four; the array branch is the only
   * one that can express a removal or a reordering.
   */
  onChange?: (
    fieldOrModel: string | Array<{ field: string; sort: string }>,
    direction?: string | null,
    override?: boolean,
  ) => void;
  /**
   * True when `sort` is still the table's DEFAULT order rather than something the user
   * chose. The control then renders as un-applied: no count chip, no clear affordance.
   *
   * The badge has to mean "you deviated from the default", not "a sort exists". A
   * server-ordered table always has a sort, so without this the badge would be permanently
   * lit and would stop carrying any information. The column header still shows the sort
   * direction either way — that is STATE, and it is a different question from deviation.
   */
  isDefault?: boolean;
}

export const Sort = (props: SortProps) => {
  const {
    apiRef,
    disabled = false,
    sort = [],
    columns = [],
    onChange = () => { },
    isDefault = false,
  } = props;

  // Presentation only. Every behavioural use of `sort` below stays on the real model — a
  // default sort is still a sort, it just isn't a user's choice to advertise or clear.
  const appliedCount = isDefault ? 0 : sort.length;
  const theme = useTheme();

  // Check if multi-column sorting is supported (DataGridPro only)
  // Since Table component sets disableMultipleColumnsSorting={false} when license is available,
  // we can rely on hasMuiXLicense check
  const [supportsMultiSort, setSupportsMultiSort] = useState(hasMuiXLicense);

  useEffect(() => {
    // Verify multi-sort is actually enabled on the DataGrid instance
    if (apiRef?.current) {
      const disableMultipleColumnsSorting = apiRef.current.instanceId?.props?.disableMultipleColumnsSorting;
      const isMultiSortEnabled = hasMuiXLicense && disableMultipleColumnsSorting !== true;
      setSupportsMultiSort(isMultiSortEnabled);
    }
  }, [apiRef, sort.length]);
  const sortColumns = columns
    .filter((col) => {
      return col.headerName != false && sort.includes(col) == false;
    })
    .map((col) => {
      // sortType lets a column declare its SORT semantics separately from its filter
      // render `type` (e.g. a boolean field rendered as a statusChip filter).
      return { label: col.headerName, value: col.field, type: col.sortType ?? col.type };
    });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const sortButtonRef = useRef(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(sortButtonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClearSort = () => {
    console.log('[Sort] handleClearSort called');
    if (apiRef) {
      console.log('[Sort] Clearing sort via apiRef');
      apiRef.current.restoreState({
        sorting: {
          sortModel: [],
        },
      });
    } else {
      console.log('[Sort] Clearing sort via onChange callback');
      onChange("all", "clear", true);
    }
    setAnchorEl(null);
  };

  const handleRemoveSort = (field) => {
    console.log('[Sort] handleRemoveSort called for field:', field);
    if (apiRef) {
      console.log('[Sort] Removing sort via apiRef');
      apiRef.current.sortColumn(field, null, true);
    } else {
      // THE WHOLE MODEL, not `(field, null, true)`.
      //
      // `null` is not a direction, and every consumer's `onChange` guards on one:
      // Shop / Support / InternalSupport all early-return on `!direction`, so the
      // row's X did NOTHING there, while Scans and Activity have no guard and wrote
      // `{ field, sort: null }` back into the model — a sort entry that still counts
      // toward the badge and orders nothing. So on all FIVE non-apiRef surfaces the
      // per-row remove was broken, in one of two ways, and neither surfaced because
      // `SortProps.onChange` was typed `() => void` (see it, now widened).
      //
      // The array shape is what all five already implement (`if (Array.isArray(col))`),
      // and it is the only one of the four call shapes that can express a REMOVAL.
      console.log('[Sort] Removing sort via onChange (model)');
      onChange(sort.filter((col: any) => col.field !== field));
    }
    if (sort.length == 1) {
      console.log('[Sort] Last sort removed, closing menu');
      setAnchorEl(null);
    }
  };

  const handleAddSort = (item) => {
    console.log('[Sort] handleAddSort called with item:', item);
    // Dates default to newest-first (desc); everything else asc.
    const defaultDir = item.type === 'dateTime' || item.type === 'date' ? 'desc' : 'asc';
    console.log('[Sort] Current sort length:', sort.length);
    console.log('[Sort] Current sort state:', sort);

    // To open the sort field meny first if nothing selected yet
    if (sort.length == 0) {
      setAnchorEl(sortButtonRef.current);
    }
    if (apiRef) {
      console.log('[Sort] Using apiRef to add sort');
      // Check if DataGrid supports multi-column sorting
      // Community version only supports single column, Pro supports multiple
      if (supportsMultiSort && sort.length > 0) {
        // Append to existing sorts (Pro version)
        const newSortModel = [...sort, { field: item.value, sort: defaultDir }];
        console.log('[Sort] New sort model (multi-sort):', newSortModel);
        apiRef.current.setSortModel(newSortModel);
      } else {
        // Replace existing sort (Community version or first sort)
        const newSortModel = [{ field: item.value, sort: defaultDir }];
        console.log('[Sort] New sort model (single-sort):', newSortModel);
        apiRef.current.setSortModel(newSortModel);
      }
    } else {
      console.log('[Sort] Using onChange callback to add sort');
      onChange(item.value, defaultDir, false);
    }
  };

  const reorder = (items: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(items);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleDragEnd = ({
    destination,
    source,
  }: {
    destination: DraggableLocation;
    source: DraggableLocation;
  }) => {
    if (!destination) return;
    const reordered = reorder(sort, source.index, destination.index);
    // GUARDED. `apiRef` is optional — five surfaces render this control with no
    // DataGrid behind it (Shop, Support, InternalSupport, Scans, Activity; see
    // `SupportListControls`' note) — so the bare `apiRef.current` here threw
    // "Cannot read properties of undefined" and took the page down the moment
    // anyone dragged a sort row on any of them. Every other handler in this file
    // already had the `if (apiRef)` fork; this one was simply missed.
    if (apiRef) {
      apiRef.current.setSortModel(reordered);
    } else {
      onChange(reordered);
    }
  };

  const handleColumnChange = (e, sortDir, originalColumn) => {
    const newSort = sort.filter((col) => col.field != originalColumn);
    if (apiRef) {
      apiRef.current.setSortModel([
        ...newSort,
        {
          field: e.target.value,
          sort: sortDir,
        },
      ]);
    } else {
      onChange([
        ...newSort,
        {
          field: e.target.value,
          sort: sortDir,
        },
      ]);
    }
  };

  const handleSortChange = (e, column) => {
    console.log('[Sort] handleSortChange called for column:', column, 'value:', e.target.value);
    if (apiRef) {
      console.log('[Sort] Changing sort via apiRef');
      apiRef.current.sortColumn(column, e.target.value, true);
    } else {
      console.log('[Sort] Changing sort via onChange callback');
      onChange(column, e.target.value, true);
    }
  };

  // Direction labels come from `@repo/ui` (`utils/sortLabels`), NOT from a copy here.
  //
  // This file used to declare its own `sortLabels` map, written `[DESC, ASC]` and read
  // back with `[1]` for ascending — so it was correct, but only by two mistakes
  // cancelling. `@repo/ui`'s map was added later "mirroring" it, got the order wrong in
  // the direction that does NOT cancel for dates and booleans, and `apps/app` (its only
  // consumer) shipped "Newest first" on the ascending option. One map, declared
  // `[ASC, DESC]` and read that way, is what stops that recurring; see that file.
  //
  // Every label this renders is unchanged — including the old map's "Low -High" typo,
  // which is now spelled with its space.

  return (
    <>
      <ButtonGroup
        variant="contained"
        disableElevation
        ref={sortButtonRef}
        color="inherit"
        sx={{
          // `& > button:first-of-type`, structurally. This read ".ButtonGroup-firstButton", which
          // matches NOTHING in this MUI version (children carry `MuiButtonGroup-grouped`), so the
          // divider never rendered — what was visible was MUI's default border in the default
          // palette's purple, until that was neutralised across the toolbar row.
          //
          /*
            🚨 THE INSET-SHADOW DIVIDER IS GONE — same false premise as Filter's, and Sort was
            the worse of the two. See `Filters.tsx` for the full account; the short version is
            that the comment above claimed `ActionLine` neutralises ButtonGroup border colours
            row-wide, so a border here could never win and a shadow was needed instead. MEASURED
            on the live page, the borders are alive: this button computed
            `border-right: 1px rgb(63,63,70)`.

            So at the seam Sort drew THREE 1px lines in THREE different colours, side by side
            with no overlap — a 3px banded edge:

              inset shadow      rgb(82,82,91)   <- this declaration
              border-right      rgb(63,63,70)   <- the explicit `borderRight` on the Sort button
              X's border-left   rgb(39,39,42)   <- the ButtonGroup's own

            The shadow goes, and so does the button's explicit `borderRight` (see below): both
            were extra declarations for a divider the ButtonGroup border already draws. The two
            facing borders are then collapsed into one by pulling the X back over Sort's edge,
            which is what MUI does for direct children and cannot do here because the X is
            wrapped in a `<Collapse>` and so never matches `:not(:first-of-type)`.
          */
          // Squared right corners ONLY while the X is actually beside it — a QUERY, not the
          // applied flag, because the X collapses out of the row before any label does
          // (VRG-535 second pass). See `toolbarClearEdgeSx`.
          "& > button:first-of-type": {
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
            ...(appliedCount > 0 ? toolbarClearEdgeSx : {}),
            // Remove problematic transitions that cause flashing
            transition: "none",
          },
          // The Collapse is the X's wrapper and Sort's actual sibling, so the overlap belongs
          // here rather than on the button — a negative margin on the button would be measured
          // inside a wrapper that has already reserved the full width, and the gap would stay.
          // Same reason the collapse rung lands here: hiding the BUTTON would leave this
          // wrapper holding its 48px and recover nothing.
          "& > .MuiCollapse-root": {
            ...toolbarClearSx(),
            marginLeft: "-1px",
          },
        }}
      >
        {appliedCount == 0 && (
          <MenuButton
            items={sortColumns}
            search={true}
            onSelect={handleAddSort}
            button={
              // TODO: get Sort to use common button component
              // `Tooltip` wrapping the Button INSIDE `MenuButton`'s `button` prop is safe:
              // MenuButton renders it as `<Box onClick>{button}</Box>` rather than
              // cloneElement-ing it, so nothing it injects is intercepted, and Tooltip emits
              // no DOM of its own.
              <Tooltip title="Sort" enterDelay={600}>
              <Button
                variant="secondary"
                size="large"
                disableTouchRipple
                disabled={disabled}
                // The accessible name. The visible "Sort" span is `display: none` once the
                // toolbar container narrows (VRG-535), which takes it out of the
                // accessibility tree as well as the layout — without this the collapsed
                // button announces as unnamed.
                aria-label="Sort"
                startIcon={
                  <ArrowUpDownIcon
                    color={theme.palette.primary?.[800] || "#454545"}
                    strokeWidth={1}
                  />
                }
                sx={{
                  // Zeroes `startIcon`'s margins while the label is hidden so the glyph
                  // centres rather than sitting 12px right of centre.
                  ...toolbarStartIconSx("sort"),
                  // borderRadius: 2,
                  // backgroundColor: theme.palette.primary?.[50] || "#f6f6f6",
                  // minHeight: 48,
                  // minWidth: 48,
                  // fontSize: 16,
                  // textTransform: "none",
                  // opacity: disabled ? 0.3 : 1,
                  // overflowY: "hidden",
                  // alignItems: "center",
                  // display: "flex",
                  // justifyContent: "center",
                  // color: theme.palette.primary?.[800] || "#454545",
                  // transition: "none",
                  // "&:hover": {
                  //   backgroundColor: theme.palette.primary?.[100] || "#e7e7e7",
                  // },
                }}
              >
                {/* Label collapses to icon-only as the TOOLBAR CONTAINER narrows — fourth
                    of the five to go (VRG-535, `toolbarCollapse.ts`). It was a VIEWPORT
                    query at `md`, which never fired at the width the row actually wraps at. */}
                <Box component="span" sx={toolbarLabelSx("sort")}>
                  Sort
                </Box>
              </Button>
              </Tooltip>
            }
          />
        )}
        {/* TODO: fix this to match the animation of Filters . I.e. we can't render this this way becasue animation is trigger on mount */}
        {appliedCount > 0 && (
          <>
            <Tooltip title={`Sort — ${appliedCount} applied`} enterDelay={600}>
            <Button
              onClick={handleClick}
              disableTouchRipple
              size="large"
              variant="secondary"
              disabled={disabled}
              /*
                The accessible name — see the un-applied branch above for why it is stated
                rather than left to the label text. It carries the COUNT because the count
                badge is a `<Typography>` inside the button, so the pre-collapse accessible
                name was the bare concatenation "Sort2".
              */
              aria-label={`Sort, ${appliedCount} applied`}
              startIcon={
                <ArrowUpDownIcon
                  color={theme.palette.primary?.[800] || "#454545"}
                  strokeWidth={1}
                />
              }
              sx={{
                // Zeroes `startIcon`'s margins while the label is hidden so the glyph centres.
                // `true` — this is the APPLIED branch, so the label is gone at every width and
                // the margins must go with it (see the label below).
                ...toolbarStartIconSx("sort", true),
                borderRadius: 2,
                // NO right-corner override here any more. The ButtonGroup's own
                // `& > button:first-of-type` rule owns the right corners and gates them on the
                // clear-X's query (`toolbarClearEdgeSx`); a second, unconditional declaration
                // here squared the edge even at widths where the X had collapsed away, leaving
                // a flat side with nothing attached to it.
                // NO `borderRight` here. It used to declare `1px solid grey[200]` — a LIGHT
                // token, so in dark it computed rgb(63,63,70) against the X's rgb(39,39,42)
                // and the two halves of one control drew the seam in two different greys.
                // The ButtonGroup's own border is the divider; this was a third declaration
                // for it. See the group's `sx` above.
                // backgroundColor: theme.palette.primary?.[50] || "#f6f6f6",
                // minHeight: 48,
                // minWidth: 48,
                // fontSize: 16,
                // textTransform: "none",
                // overflowY: "hidden",
                // opacity: disabled ? 0.3 : 1,
                // alignItems: "center",
                // display: "flex",
                // justifyContent: "center",
                // color: theme.palette.primary?.[800] || "#454545",
                // transition: "none",
                // "&:hover": {
                //   backgroundColor: theme.palette.primary?.[100] || "#e7e7e7",
                // },
              }}
            >
              {/* This is the APPLIED branch, so the label is hidden at EVERY width, not just
                  below the `sort` rung — the count badge immediately below has taken its
                  place. Keeping both made this the widest control in the row exactly when the
                  row could least afford it (measured: +162px across Filter and Sort together,
                  which no single threshold table can absorb). The tooltip and `aria-label`
                  above still read "Sort — N applied". See the applied-state section of
                  `toolbarCollapse.ts`. */}
              <Box component="span" sx={toolbarLabelSx("sort", "inline", true)}>
                Sort
              </Box>
              {/* TODO: make label a component - this is recreating a lot of palces */}
              <Zoom
                in={appliedCount > 0 && !disabled}
                timeout={200}
                mountOnEnter
                unmountOnExit
              >
                <Stack
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: 45,
                    margin: 1,
                    marginLeft: 3,
                    background:
                      "linear-gradient(90deg, rgba(130,90,195,1) 0%,rgb(55, 39, 137) 100%)",
                    fontSize: 2,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    color="common.white"
                    sx={{ fontSize: 12, fontWeight: 400 }}
                  >
                    {appliedCount}
                  </Typography>
                </Stack>
              </Zoom>
            </Button>
            </Tooltip>
            <Collapse
              in={appliedCount > 0}
              orientation="horizontal"
              timeout={500}
              mountOnEnter
              unmountOnExit
            >
              <Button
                variant="secondary"
                onClick={handleClearSort}
                disableTouchRipple
                disabled={disabled}
                sx={{
                  borderRadius: 2,
                  borderTopLeftRadius: 0,
                  // NO backgroundColor. This is the other half of the Sort control and must
                  // read as one surface with it — and the Sort button sets none, taking its
                  // fill from `variant="secondary"`. Naming a colour here made the two halves
                  // agree only by coincidence: it was `primary[50]` (too close to the page in
                  // light), then `background.paper`, which is not what the secondary variant
                  // paints in dark — so the X sat visibly darker than the button it is
                  // attached to. Inheriting the variant makes them match by construction
                  // rather than by two values happening to be equal.
                  borderBottomLeftRadius: 0,
                  opacity: disabled ? 0.3 : 1,
                  padding: 1,
                  fontSize: 16,
                  minHeight: TOOLBAR_CONTROL_HEIGHT,
                  minWidth: 64,
                  height: TOOLBAR_CONTROL_HEIGHT,
                  width: 48,
                  // 20px, a step below the sort icon's 24 — a clear affordance should not carry the
                  // same weight as the control it clears. `flexShrink: 0` is what actually does it:
                  // width/height were already declared and the glyph still computed to 14px wide,
                  // because flexbox was SHRINKING it, not overriding it.
                  "& svg": { width: 20, height: 20, flexShrink: 0 },
                  color: theme.palette.primary?.[800] || "#454545",
                  transition: "none",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.error?.main || "#d32f2f",
                  },
                }}
              >
                {/* Sized to the button's own 16px label. Lucide defaults to 24, which made
                    this X noticeably larger than the one on each row in the menu below. */}
                <XIcon strokeWidth={1} size={16} />
              </Button>
            </Collapse>
          </>
        )}
      </ButtonGroup>
      <Menu
        id="sort-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        sx={{
          pb: 0,
          pt: 0,
        }}
        MenuListProps={{
          "aria-labelledby": "sort-button",
          sx: {
            pb: 0,
            pt: 0,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 2,
              p: 2,
              borderRadius: 2,
              boxShadow: `rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px`,
              overflow: "visible",
              // Grow to fit the widest sort row: the Selects size to their labels (a column can be
              // "Last ownership date"), so a fixed-width paper clipped them and the row read as two
              // overlapping chips. Capped so a pathological name cannot run off screen.
              width: "max-content",
              maxWidth: "min(560px, calc(100vw - 32px))",
              // Dark: background.paper (#0f0f12). Light: pure WHITE (not the #fafafa
              // paper grey) so the faint-grey primary[50] field buttons inside read as
              // distinct chips instead of blending into a same-grey panel.
              backgroundColor: (theme) =>
                theme.palette.mode === "light" ? "#ffffff" : theme.palette.background.paper,
              backgroundImage: "none",
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {appliedCount > 0 && (
          <>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable">
                {(
                  provided: DroppableProvided,
                  snapshot: DroppableStateSnapshot
                ) => (
                  <Stack
                    justifyContent="flex-start"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      background: snapshot.isDraggingOver
                        ? theme.palette.white[100]
                        : "transparent",
                    }}
                    sx={{ overflowX: "visible", flexShrink: 0, pb: 2 }}
                  >
                    {sort.map((item: any, index: number) => (
                      <Draggable
                        key={item.field}
                        index={index}
                        draggableId={item.field}
                      >
                        {(
                          provided: DraggableProvided,
                          snapshot: DraggableStateSnapshot
                        ) => {
                          const column = columns.filter(
                            (col) => col.field == item.field
                          )[0];
                          // sortType overrides filter-render type for sort semantics.
                          const columnType = column?.sortType ?? column?.type;
                          // `[ascendingLabel, descendingLabel]`.
                          const [ascLabel, descLabel] =
                            getSortDirectionLabels(columnType);
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                background: snapshot.isDragging
                                  ? theme.palette.grey[100]
                                  : "transparent",
                              }}
                            >
                              <Stack
                                key={index}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={3}
                                py={1.5}
                                pr={1}
                                {...provided.dragHandleProps}
                              >
                                <Stack
                                  alignItems="center"
                                  direction="row"
                                  spacing={3}
                                >
                                  {/* The icon is an inline SVG, so the Box's line-height
                                      left it sitting a couple of px above the row's centre
                                      even inside an alignItems="center" Stack. Making the
                                      Box a flex container centres the glyph itself. */}
                                  <Box px={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <GripVerticalIcon strokeWidth={1} />
                                  </Box>
                                  <Select
                                    size="compact"
                                    defaultValue={item.field}
                                    value={item.field}
                                    onChange={(value) =>
                                      handleColumnChange(
                                        { target: { value } },
                                        item.sort,
                                        item.field
                                      )
                                    }
                                  >
                                    {columns
                                      .filter((col) => col.headerName)
                                      .map((col) => (
                                        <MenuItem
                                          key={col.field}
                                          value={col.field}
                                          sx={{
                                            display:
                                              col.field == item.field
                                                ? "none"
                                                : "block",
                                            fontSize: "16px",
                                          }}
                                        >
                                          {col.headerName}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                  <Select
                                    size="compact"
                                    // Wide enough for the longest direction label
                                    // ("Newest first" / "Oldest first"), which otherwise
                                    // ran under the dropdown chevron.
                                    sx={{ minWidth: 148 }}
                                    defaultValue={item.sort}
                                    value={item.sort}
                                    onChange={(value) =>
                                      handleSortChange({ target: { value } }, item.field)
                                    }
                                  >
                                    <MenuItem
                                      key={`${item.field}-asc`}
                                      value={"asc"}
                                      sx={{ fontSize: "16px" }}
                                    >
                                      {ascLabel}
                                    </MenuItem>
                                    <MenuItem
                                      key={`${item.field}-desc`}
                                      value={"desc"}
                                      sx={{ fontSize: "16px" }}
                                    >
                                      {descLabel}
                                    </MenuItem>
                                  </Select>
                                </Stack>

                                <IconButton
                                  color="error"
                                  aria-label="remove sort"
                                  onClick={() => handleRemoveSort(item.field)}
                                  edge="start"
                                  sx={{
                                    color: theme.palette.grey[900],
                                    "&:hover": {
                                      backgroundColor: alpha(
                                        theme.palette.red[500],
                                        0.05
                                      ),
                                      color: theme.palette.error.main,
                                    },
                                  }}
                                >
                                  {/* Same 16 as the toolbar's clear X — both sit beside
                                      16px text, so a 24px glyph read oversized next to it. */}
                                  <XIcon strokeWidth={1} size={16} />
                                </IconButton>
                              </Stack>
                            </div>
                          );
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
            {/* Only show "Add Sort" button if multi-column sorting is supported */}
            {supportsMultiSort && (
              <MenuButton
                items={sortColumns}
                search={true}
                // fullWidth={true}
                sx={{marginLeft: '-330px'}}
                onSelect={handleAddSort}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                button={
                  <Button
                    disableElevation
                    variant="text"
                    size="large"
                    color="inherit"
                    sx={{ mb: 2, width: "100%", justifyContent: "left", fontSize: "16px" }}
                    onClick={handleClick}
                    startIcon={
                      <PlusIcon
                        strokeWidth={1}
                        style={{ boxShadow: "none", border: "none !important" }}
                      />
                    }
                  >
                    Add Sort
                  </Button>
                }
              />
            )}
            <Button
              disableElevation
              variant="secondary"
              fullWidth
              disableTouchRipple
              size="large"
              onClick={handleClearSort}
              sx={{ fontSize: "16px" }}
            >
              Clear
            </Button>
          </>
        )}
      </Menu>
    </>
  );
};
